import { Dropdown, Accordion, Button, Form, Spinner } from 'react-bootstrap';
import { useRef, useState, ChangeEvent, MouseEvent, useEffect } from 'react';
import { apiService } from '../../../../services/api';

// Interface para as props do componente
export interface FilterComponentProps {
    onApplyFilters: (statusFilters: string[]) => void;
    initialFilters?: Record<string, boolean>;
    isLoading?: boolean; // Prop para indicar estado de loading
}

const FilterComponent: React.FC<FilterComponentProps> = ({
    onApplyFilters,
    initialFilters,
    isLoading = false // Valor padrão é false
}) => {
    // Estados para controlar os checkboxes e a lista de status
    const [statusList, setStatusList] = useState<any[]>([]);
    const [filtroStatus, setFiltroStatus] = useState<Record<string, boolean>>({});
    const [loadingStatus, setLoadingStatus] = useState(true);

    // Estado para loading otimista ao aplicar filtros
    const [applyingFilters, setApplyingFilters] = useState(false);

    // Referência para o dropdown
    const dropdownRef = useRef<HTMLAnchorElement>(null);

    // Resetar loading otimista quando kanban terminar de carregar
    useEffect(() => {
        if (!isLoading && applyingFilters) {
            setApplyingFilters(false);
        }
    }, [isLoading, applyingFilters]);

    // Carregar lista de status da API
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                setLoadingStatus(true);
                const response = await apiService.getStatusOportunidades();

                console.log("🔍 FILTRO - Resposta da API de Status:", response);

                if (response.success) {
                    console.log("✅ FILTRO - Total de status carregados:", response.data.length);
                    console.log("📋 FILTRO - Lista de status:", response.data.map((s: any) => ({
                        id: s.id,
                        nome: s.nome,
                        e_final: s.e_final,
                        ativo: s.ativo
                    })));

                    setStatusList(response.data);

                    // Inicializar o estado dos filtros
                    const initial: Record<string, boolean> = {};
                    response.data.forEach((status: any) => {
                        // Por padrão, selecionamos apenas status que não são finais
                        // Ou respeitamos o initialFilters se fornecido
                        if (initialFilters && status.nome in initialFilters) {
                            initial[status.nome] = initialFilters[status.nome];
                        } else {
                            initial[status.nome] = !status.e_final;
                        }
                    });

                    console.log("🎯 FILTRO - Estado inicial dos filtros:", initial);
                    setFiltroStatus(initial);

                    // Log dos status que serão aplicados por padrão
                    const statusAtivosInicial = Object.entries(initial)
                        .filter(([_, isActive]) => isActive)
                        .map(([status]) => status);
                    console.log("✅ FILTRO - Status ativos por padrão:", statusAtivosInicial);
                }
            } catch (error) {
                console.error('❌ FILTRO - Erro ao carregar status:', error);
            } finally {
                setLoadingStatus(false);
            }
        };

        fetchStatus();
    }, []);
    
    // Função para lidar com as mudanças nos checkboxes
    const handleCheckboxChange = (nomeStatus: string, checked: boolean) => {
        setFiltroStatus(prev => ({
            ...prev,
            [nomeStatus]: checked
        }));
    };

    // Função para resetar os filtros
    const resetarFiltros = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const novosFiltros: Record<string, boolean> = {};
        statusList.forEach(status => {
            novosFiltros[status.nome] = !status.e_final;
        });
        setFiltroStatus(novosFiltros);

        const statusAtivos = statusList
            .filter(s => novosFiltros[s.nome])
            .map(s => s.nome);

        onApplyFilters(statusAtivos);
    };

    // Função para aplicar os filtros
    const aplicarFiltros = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Ativar loading otimista imediatamente
        setApplyingFilters(true);

        const statusAtivos = Object.entries(filtroStatus)
            .filter(([_, isActive]) => isActive)
            .map(([status]) => status);

        console.log("🚀 FILTRO - Aplicando filtros:", {
            statusAtivos,
            totalSelecionados: statusAtivos.length,
            totalDisponiveis: statusList.length,
            todosOsFiltros: filtroStatus
        });

        onApplyFilters(statusAtivos);

        // Fechar o dropdown
        try {
            const dropdownElement = document.getElementById('dropdown-filter');
            if (dropdownElement) {
                dropdownElement.click();
            }
        } catch (error) {
            console.error('Erro ao fechar dropdown:', error);
        }
    };

    // Loading do botão de filtro (só mostra loading se está carregando lista de status ou aplicando filtros)
    const isButtonLoading = applyingFilters || loadingStatus;

    // Loading dos controles internos (desabilita se kanban está recarregando)
    const isControlsDisabled = applyingFilters || isLoading || loadingStatus;

    return (
        <Dropdown className="form-sorts dropdown me-2">
            <Dropdown.Toggle
                as="a"
                className="dropdown-toggle"
                id="dropdown-filter"
                ref={dropdownRef}
            >
                {isButtonLoading ? (
                    <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-1"
                            role="status"
                            aria-hidden="true"
                        />
                        <i className="ti ti-filter-share me-1" />
                        {applyingFilters ? 'Aplicando...' : 'Carregando...'}
                    </>
                ) : (
                    <>
                        <i className="ti ti-filter-share" />
                        Filtro
                    </>
                )}
            </Dropdown.Toggle>

            <Dropdown.Menu className="filter-dropdown-menu p-3" style={{ minWidth: '250px' }}>
                <div className="filter-set-view">
                    <div className="filter-set-head">
                        <h4>
                            <i className="ti ti-filter-share" />
                            Filtro de Status
                        </h4>
                    </div>
                    <Accordion defaultActiveKey="0" className="filter-accordion">
                        <Accordion.Item eventKey="0" className="filter-set-content">
                            <Accordion.Header className="filter-set-content-head">
                                Status da Oportunidade
                            </Accordion.Header>
                            <Accordion.Body className="filter-set-contents">
                                <div className="filter-content-list">
                                    {loadingStatus ? (
                                        <div className="text-center py-2">
                                            <Spinner animation="border" size="sm" />
                                        </div>
                                    ) : (
                                        <ul>
                                            {statusList.map((status) => (
                                                <li key={status.id}>
                                                    <div className="filter-checks">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id={`status-${status.id}`}
                                                            label={status.nome}
                                                            checked={filtroStatus[status.nome] || false}
                                                            onChange={(e) => handleCheckboxChange(status.nome, e.target.checked)}
                                                            disabled={isControlsDisabled}
                                                        />
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                    <div className="filter-reset-btns">
                        <div className="row">
                            <div className="col-6">
                                <Button
                                    variant="light"
                                    onClick={resetarFiltros}
                                    type="button"
                                    className="w-100"
                                    disabled={isControlsDisabled}
                                >
                                    Limpar
                                </Button>
                            </div>
                            <div className="col-6">
                                <Button
                                    variant="primary"
                                    onClick={aplicarFiltros}
                                    type="button"
                                    className="w-100"
                                    disabled={isControlsDisabled}
                                >
                                    {applyingFilters ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                className="me-1"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Aplicando...
                                        </>
                                    ) : (
                                        'Aplicar'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default FilterComponent;
